import {
  Charity,
  FetchTeamPageQuery,
  FetchTeamPageQueryVariables,
} from '../../../types/graphql';
import {
  IAppState,
  ICharityData,
  ILockData,
  ITeamData,
  TMembershipPolicy,
} from '../../types/state.d';
import client, { hasUnauthenticatedError } from '../../apolloClient';
import {
  getEventRequestSuccess,
  locksRequestSuccess,
  teamRequestError,
  teamRequestMade,
  teamRequestSuccess,
} from '../actionCreators/apiActions';

import { Action } from 'redux';
import { Auth } from '@justgiving/auth';
import { ERROR_MESSAGES } from '../../consts';
import TeamLockTypes from '../../consts/lockTypes';
import { ThunkDispatch } from 'redux-thunk';
import gql from 'graphql-tag';
import { validate as isValidUUID } from 'uuid';
import { parseStory } from '../../utils/mapStory';
import { redirectByResponseStatus } from '../../utils';

const fetchTeamData = (teamGuid: string) => async (
  dispatch: ThunkDispatch<IAppState, Auth, Action>,
  _state: () => IAppState,
  auth: Auth,
) => {
  try {
    await auth.refreshAccessTokenIfExpired();
    dispatch(teamRequestMade());
    const teamPage = await fetchTeam(teamGuid);

    const state: ITeamData = {
      teamGuid: teamPage?.legacyId ?? null,
      name: teamPage?.title ?? '',
      status: teamPage?.status?.toLocaleLowerCase() ?? '',
      membershipPolicy: mapMembershipPolicy(teamPage?.membershipPolicy),
      story: parseStory(teamPage?.storyWithType),
      coverPhotoImageId:
        imageIdWithExtensionFromUrl(teamPage?.cover?.url) ?? '',
      shortName: teamPage?.slug?.split('/').pop() ?? '',
      productType: teamPage?.product?.name,
      occasion: teamPage?.occasion ?? null,
      fundraisingConfiguration: {
        currencyCode: teamPage?.targetWithCurrency?.currencyCode ?? 'GBP',
        targetAmount: teamPage?.targetWithCurrency?.value
          ? (teamPage?.targetWithCurrency?.value ?? 0) / 100
          : undefined,
      },
      captain: {
        firstName: teamPage?.owner?.name?.split(' ')[0] ?? null,
        lastName: teamPage?.owner?.name?.split(' ')[1] ?? null,
        userGuid: teamPage?.owner?.legacyId ?? '',
        profileImage: imageIdWithExtensionFromUrl(teamPage?.owner?.avatar),
      },
      donationSummary: {
        totalAmount: Math.round(
          (teamPage?.donationSummary?.totalAmount?.value ?? 0) / 100,
        ),
      },
      charity: {},
      campaign: {},
    };

    setTeamRelationships(state, teamPage?.relationships, dispatch);
    setTeamLocks(teamGuid, teamPage?.relationships, dispatch);

    dispatch(
      teamRequestSuccess({
        ...state,
        teamGuid,
      }),
    );
  } catch (error) {
    dispatch(teamRequestError(ERROR_MESSAGES.GET_PAGE_DATA));
    throw error;
  }
};

export default fetchTeamData;

function setTeamLocks(
  teamGuid: string,
  teamRelationships: NonNullable<FetchTeamPageQuery['page']>['relationships'],
  dispatch: ThunkDispatch<IAppState, Auth, Action>,
) {
  const locksData: ILockData = {
    locks: [],
    configurable: [
      TeamLockTypes.Campaign,
      TeamLockTypes.Charity,
      TeamLockTypes.Event,
    ],
  };

  if (teamRelationships) {
    const lockedRelationships = teamRelationships.campaigns.nodes
      .concat(teamRelationships.charities.nodes)
      .concat(teamRelationships.events.nodes)
      .filter((x) => x.requiredForChildren);

    lockedRelationships.forEach((relationship) => {
      if (relationship?.page && relationship?.page?.product?.name) {
        locksData.locks.push({
          id: relationship.page.externalId ?? relationship.page.legacyId,
          type: getLockTypeFromProduct(relationship.page.product.name) ?? '',
          legacyId: relationship.page.legacyId,
        });
      }
    });
  }

  dispatch(
    locksRequestSuccess({
      ...locksData,
      teamGuid,
    }),
  );
}

function setTeamRelationships(
  teamData: ITeamData,
  teamRelationships: NonNullable<FetchTeamPageQuery['page']>['relationships'],
  dispatch: ThunkDispatch<IAppState, Auth, Action>,
) {
  if (!teamData.teamGuid) {
    return;
  }

  const campaignNode = teamRelationships?.campaigns.nodes.at(0);
  const campaign =
    campaignNode && campaignNode.requiredForChildren
      ? campaignNode.page
      : undefined;

  if (campaign) {
    teamData.campaign = {
      campaignGuid: campaign.legacyId,
      logo: campaign.logo ?? '',
      shortName: campaign.slug ?? '',
      summary: campaign.summary ?? '',
      title: campaign.title,
    };
  }

  const charity = teamRelationships?.charities.nodes.at(0)?.requiredForChildren
    ? teamRelationships?.beneficiaries.nodes.filter(isCharity)[0]
    : undefined;

  if (charity) {
    teamData.charity = mapCharity(charity);
  }

  const eventNode = teamRelationships?.events.nodes.at(0);
  const event =
    eventNode && eventNode.requiredForChildren ? eventNode.page : undefined;

  if (!event) return;

  teamData.event = {
    eventId: Number(event.externalId),
  };

  dispatch(
    getEventRequestSuccess({
      id: Number(event?.externalId),
      completionDate: null,
      description: null,
      eventType: event?.activity?.type ?? null,
      expiryDate: event.endDate ? mapDate(event.endDate) : null,
      location: null,
      name: event?.activity?.name,
      startDate: event.launchDate ? mapDate(event.launchDate) : null,
    }),
  );
}

async function fetchTeam(teamId: string) {
  const result = await client.query<
    FetchTeamPageQuery,
    FetchTeamPageQueryVariables
  >({
    query: fetchTeamPageQuery,
    variables: {
      teamId,
    },
  });

  if (result.errors && hasUnauthenticatedError(result.errors)) {
    redirectByResponseStatus(401);
  }

  if (result.errors) {
    throw new Error(ERROR_MESSAGES.GET_PAGE_DATA);
  }

  return result.data.page;
}

const mapCharity = (charity: Charity): ICharityData => {
  if (!charity.externalId) {
    throw new Error('charity ID is not set');
  }

  return {
    charityId: charity.externalId,
    logo: charity.logo ?? '',
    name: charity.name ?? '',
    operatingCompanyCode: charity.countryCode ?? '',
  };
};

const mapDate = (date: string): string | null => {
  if (!date) return null;
  return new Date(date).getTime().toString();
};

function isCharity(value?: any): value is Charity {
  return value?.__typename === 'Charity';
}

function getLockTypeFromProduct(productType: string) {
  switch (productType) {
    case 'proxy_charity':
      return TeamLockTypes.Charity;
    case 'proxy_campaign':
    case 'campaign':
      return TeamLockTypes.Campaign;
    case 'proxy_event':
      return TeamLockTypes.Event;
    default:
      return undefined;
  }
}

const mapMembershipPolicy = (
  policy: string | undefined | null,
): TMembershipPolicy => {
  switch (policy) {
    case 'open':
    case 'invite-only':
      return policy;
    default:
      return 'invite-only';
  }
};

const imageIdWithExtensionFromUrl = (
  url: string | undefined | null,
): string | null => {
  const id = url?.split('/').pop();
  if (!id) return null;
  if (!isValidUUID(id.split('.')[0])) return null;
  return id;
};

const fetchTeamPageQuery = gql`
  fragment ParentRelationship on PageRelationship {
    type
    requiredForChildren
    page {
      id
      legacyId
      slug
      url
      logo
      title
      summary
      externalId
      launchDate
      endDate
      activity {
        name
        type
      }
      branding {
        logo
      }
      product {
        name
      }
    }
  }

  query FetchTeamPage($teamId: ID!) {
    page(type: TEAM, id: $teamId) {
      legacyId
      slug
      title
      status
      membershipPolicy
      product {
        name
      }
      occasion {
        type
      }
      storyWithType {
        type
        content
      }
      owner {
        legacyId
        name
        avatar
      }
      targetWithCurrency {
        value
        currencyCode
      }
      cover {
        ... on ImageMedia {
          id
          url
          alt
          caption
        }
        ... on VideoMedia {
          id
          url
          embeddedVideoURL
        }
        ... on LivestreamMedia {
          id
          url
          embeddedStreamURL
          embeddedStreamChatURL
        }
      }
      donationSummary {
        totalAmount {
          value
          currencyCode
        }
        onlineAmount {
          value
          currencyCode
        }
      }
      relationships {
        beneficiaries(first: 1) {
          nodes {
            ... on Charity {
              impactStatement
              id
              legacyId
              externalId
              logo
              name
              profilePageUrl
              slug
              countryCode
            }
          }
        }
        campaigns: parentsConnection(productName: CAMPAIGN, first: 1) {
          nodes {
            ...ParentRelationship
          }
        }
        events: parentsConnection(productName: EVENT, first: 1) {
          nodes {
            ...ParentRelationship
          }
        }
        charities: parentsConnection(productName: CHARITY, first: 1) {
          nodes {
            ...ParentRelationship
          }
        }
      }
    }
  }
`;
